/**
 * @module app.um
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { apiCall } from 'app/shared/lib/essette-api';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { AuthService } from 'app/um/services/auth-service';
import { DateTimeValue } from 'app/shared/lib/date-time';
import { localSort } from 'app/shared/lib/sorting';
import * as UM from 'app/um/models/essette-api';

interface CustomAuthStatusHistory extends UM.AuthStatusHistory {
    originalStatusDate: string | null;
}

@autoinject()
export class AuthStatusHistory extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.stringNull({ changeHandler: 'reload' })
    protected authId: string | null = null;

    @bindable.boolean({ defaultBindingMode: bindingMode.toView })
    protected headerLevelTat: boolean = true;

    protected authStatusHistory: CustomAuthStatusHistory[] = [];

    private localSortProperties: string[] = ['originalStatusDate'];
    private apiSorting: Sorting = new Sorting();
    private localSorting: Sorting = new Sorting();
    protected resultCount = 0;
    protected pageSize = 10;


    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: AuthService,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        this.reload();
    }


    ////****************** Events ***************************************************////
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;

        //If LocalSort
        if(this.localSortProperties.indexOf(property) > -1) {
            const { localSorting } = this;
            const authStatusHistory: CustomAuthStatusHistory[] = <CustomAuthStatusHistory[]>this.authStatusHistory;
            if(direction === null) {
                this.localSorting.remove(property);
            } else {
                this.localSorting.add({
                    reference: property,
                    direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
                });
            }
            localSort(authStatusHistory, localSorting);
            if(this.localSorting.entries.length == 0) {
                this.reload();
            };
        }
        // Else If API Sort
        else {
            const { apiSorting } = this;
            if(direction === null) {
                apiSorting.remove(property);
            } else {
                apiSorting.add({
                    reference: property,
                    direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
                });
            }
            this.reload();
        }
    }


    ////****************** Functions ************************************************////
    protected async reload(): Promise<void> {
        await this.loadAuthStatusHistory();
    }

    protected isTatMet(status: UM.AuthStatusHistory): string {
        const endTimestamp = status?.metadata?.ended?.timestamp ? new DateTimeValue(status.metadata.ended.timestamp) : null;
        const dueDateTime = status.dueDateTime ? new DateTimeValue(status.dueDateTime) : null;
        const now = new DateTimeValue(this.env.now());
        const isOverdue = dueDateTime ? ((endTimestamp && endTimestamp.value) ? endTimestamp.isAfter(dueDateTime) : now.isAfter(dueDateTime)) : false;
        const isDetermination = status.status?.determination?.result !== "none";

        if(!dueDateTime) { return 'u'; }
        else if(isOverdue) { return 'n'; }
        else if(isDetermination) { return 'm'; }
        else if(!endTimestamp) { return 'u'; }
        else { return 'm' }
    }


    ////****************** Service Calls ********************************************////
    @apiCall
    protected async loadAuthStatusHistory(): Promise<void> {
        if(!this.authId) return;

        const options: Partial<IQueryLoadOptions> = {
            top: this.pageSize,
            page: this.$pager?.page || 0,
            sorting: this.apiSorting
        };

        const includes = [
            'audit.start',
            'audit.start.user',
            'status',
            'priority',
            'metadata.started.byUser',
            'turnAroundTime',
            'metadata.effectiveOverridden.byUser',
            'status.reason'
        ];

        try {
            this.state.beginLoad();

            const response = await this.service.loadAuthStatusHistory(this.authId, options, includes);
            this.authStatusHistory = response.results.map(result => {
                return { ...result, originalStatusDate: result.metadata?.effectiveOverridden?.timestamp ? result.audit?.start?.timestamp || null : null };
            });
            this.resultCount = response.count || 0;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }
}
